import { useState } from "react";
import { format } from "date-fns";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import clsx from "clsx";
import { Calendar, ChevronLeft, ChevronRight, CircleX } from "lucide-react";
import { cn, MONTHS } from "@/lib/utils";

const MonthYearPicker = ({
  value,
  onChange,
  placeholder,
  onClear,
  disabled,
}: {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  onClear: () => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const handleSelect = (monthIndex: number) => {
    const selectedDate = new Date(year, monthIndex, 1);
    onChange(selectedDate);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal h-[40px]",
            value
              ? "text-black hover:text-black"
              : "text-g-400 hover:text-g-400"
          )}
        >
          {value
            ? format(value, "MMM yyyy")
            : placeholder ?? "Select month/year"}
          {disabled ? (
            <></>
          ) : value ? (
            <div
              className="h-5 w-5 cursor-pointer flex items-center justify-end"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }}
            >
              <CircleX className="stroke-gray-500 group-hover:stroke-black" />
            </div>
          ) : (
            <Calendar className="text-g-300 ml-3" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-4">
        <div className="flex justify-between items-center mb-3">
          <button
            onClick={() => setYear((y) => y - 1)}
            className="px-2 py-1 text-gray-500 hover:text-black"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-semibold">{year}</span>
          <button
            onClick={() => setYear((y) => y + 1)}
            className="px-2 py-1 text-gray-500 hover:text-black"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((month, idx) => (
            <button
              key={month}
              onClick={() => handleSelect(idx)}
              className={clsx(
                "p-3 rounded text-sm",
                value &&
                  value?.getFullYear() === year &&
                  value?.getMonth() === idx
                  ? "bg-primary text-white"
                  : "hover:bg-muted"
              )}
            >
              {month}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MonthYearPicker;
